import { useState, useEffect } from 'react';
import { getUserStats } from '../services/api';
import { FolderIcon, ZapIcon, TrendingUpIcon } from 'lucide-react';


export default function StatsSidebar({ user }) {
  // Real data instead of mock data
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalPrompts: 0,
    weeklyActivity: 0,
    trends: {
      projects: 'Loading...',
      prompts: 'Loading...',
      activity: 'Loading...'
    },
    loading: true
  });

  useEffect(() => {
    if (user?.id) {
      loadUserStats();
    }
  }, [user]);

  const loadUserStats = async () => {
    try {
      setStats(prev => ({ ...prev, loading: true }));
      const data = await getUserStats(user.id);
      setStats({
        totalProjects: data.totalProjects,
        totalPrompts: data.totalPrompts,
        weeklyActivity: data.weeklyActivity,
        trends: data.trends || {
          projects: 'No data',
          prompts: 'No data', 
          activity: 'No data'
        },
        loading: false
      });
    } catch (error) {
      console.error('Error loading user stats:', error);
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  if (stats.loading) {
    return (
      <div className="w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700 p-4 space-y-4">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-28 mb-3"></div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="mb-4 last:mb-0">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-2"></div>
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-12 mb-1"></div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const statItems = [
    {
      label: 'Active Projects',
      value: stats.totalProjects,
      trend: stats.trends.projects,
      icon: FolderIcon,
      color: 'text-celadon-600',
      bgColor: 'bg-celadon-50',
      iconColor: 'text-celadon-600'
    },
    {
      label: 'Total Prompts',
      value: stats.totalPrompts,
      trend: stats.trends.prompts,
      icon: ZapIcon,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      iconColor: 'text-gray-600'
    },
    {
      label: 'Weekly Activity',
      value: stats.weeklyActivity,
      trend: stats.trends.activity,
      icon: TrendingUpIcon,
      color: 'text-gray-700',
      bgColor: 'bg-gray-100',
      iconColor: 'text-gray-700'
    }
  ];

  return (
    <div className="w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700 p-4 h-fit sticky top-24">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">Workspace Overview</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">Your current activity and progress</p>
      </div>
      
      <div className="space-y-4">
        {statItems.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <div key={index} className="group hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-xl p-3 -m-3 transition-all duration-200 animate-slide-up" style={{ animationDelay: `${index * 100}ms` }}>
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 ${stat.bgColor} dark:bg-gray-700/50 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-200`}>
                  <IconComponent className={`${stat.iconColor} dark:text-gray-400 w-4 h-4`} />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{stat.label}</p>
                  <p className={`text-xl font-bold ${stat.color} dark:text-gray-200 mb-1`}>{stat.value}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{stat.trend}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
